<template>
  <div>
    <GkLogo :full="false" id="butler-tt" :fill="theme" />
    <b-popover
      :target="'butler-tt'" 
      boundary="window"
      :placement="popoverPlacement"
      :fallback-placement="[popoverPlacement]"
      :triggers="(isTyping || finalLine) ? 'manual' : 'hover'"
      :custom-class="'popover-light popover-butler ' + ((placed) ? 'placed popover-brand' : '')" 
    >
      <div @click="checkFinal" class="popover-text" ref="butler-popover-text" v-html="renderedMessage" />
    </b-popover>
  </div>
</template>

<script>
import GkLogo from '@/components/ui/GkLogo.vue'
export default {
  name: 'GkButler',
  components: {
    GkLogo
  },
  props: {
    nextLine: {
      type: String,
      default: null
    },
    theme: {
      type: String,
      default: 'brand'
    },
    silent: {
      type: Boolean,
      default: false
    },
    placed: {
      type: Boolean,
      default: false
    },
    finalLine: {
      type: Boolean,
      default: false
    },
    mobileView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      splitWords: [],
      typingSpeed: 10,
      typeTimeout: null,
      hideTimeout: null,
      letterIndex: 0,
      wordIndex: 0,
      ttKey: 0,
      messageHeight: '0px',
      renderedMessage: null,
      isTyping: false
    };
  },
  computed: {
    messageShowTime () {
      const wordsPerSecond = 7
      const minTimeout = 1000
      const maxTimeout = 5000
      const timeout = Math.max(
        minTimeout, 
        Math.min((this.splitWords.length / wordsPerSecond) * 1000, maxTimeout)
      )
      return timeout
    },
    popoverPlacement () {
      let placement = ''
      if (this.placed) {
        placement = 'bottomright'
      } else {
        if (this.mobileView) {
          placement = 'topright'
        } else {
          placement = 'right'
        }
      }
      return placement
    }
  },
  watch: {
    nextLine (newValue) {
      if (newValue && newValue !== '') {
        if (this.finalLine) {
          // wait for butler to finish moving
          this.$root.$emit('bv::hide::popover', 'butler-tt')
          setTimeout(() => {
            this.startTyping()
          }, 500)
        } else {
          this.startTyping()
        }
      }
    }
  },
  created () {
  },
  beforeDestroy () {
    clearTimeout(this.typeTimeout)
    clearTimeout(this.hideTimeout)
  },
  mounted () {
    if (this.nextLine) {
      this.startTyping()
    }
  },
  methods: {
    startTyping () {
      this.isTyping = true
      setTimeout(() => {
        // timeout to ensures popover component is mounted before trying to ::show()
        clearTimeout(this.typeTimeout)
        clearTimeout(this.hideTimeout)
        this.emptyMessageBox()
        this.$root.$emit('bv::show::popover', 'butler-tt')
        this.letterIndex = 0
        this.wordIndex = 0
        this.splitWords = this.nextLine.split(' ')
        setTimeout(() => {
          // timeout to give popover time to render after ::show()
          this.typeText()
        }, 100)
      }, 300)
    },
    typeText() {
      if (this.silent) return
      if (this.wordIndex < this.splitWords.length) {
        if (this.letterIndex === 0) {
          // add a new word-span and update counters
          this.addWordSpan()
        }
        if (this.letterIndex < this.splitWords[this.wordIndex].length) {
          this.addMessageLetter(this.splitWords[this.wordIndex][this.letterIndex])
          this.letterIndex++
          if (this.letterIndex >= this.splitWords[this.wordIndex].length) {
            this.wordIndex++
            this.letterIndex = 0
          } 
          this.typeTimeout = setTimeout(this.typeText, this.typingSpeed)
        }
      } else {
        if (this.finalLine) {
          this.addFinalButton()
        }

        /**
         * After typing is complete wait x time before hidding the popover
         * and emit event (to place butler when splash screen)
         */
        this.hideTimeout = setTimeout(() => {
          const popover_text = this.$refs['butler-popover-text']
          if (popover_text && !this.finalLine) {
            this.renderedMessage = popover_text.innerHTML
          }
          this.isTyping = false
          if (!this.finalLine) {
            this.$root.$emit('bv::hide::popover', 'butler-tt')
          }
          if (!this.placed && !this.finalLine) {
            this.$emit('finished-typing', false)
          }
        }, this.messageShowTime);
      }
    },
    addWordSpan () {
      const popover_text = this.$refs['butler-popover-text']
      const span = document.createElement('span')
      span.id = 'word-span-' + this.wordIndex
      span.classList.add('butler-msg-word')
      if (popover_text) {
        popover_text.appendChild(span)
      }
    },
    addMessageLetter (letter) {
      const popover_word = document.getElementById('word-span-' + this.wordIndex)
      const span = document.createElement('span')
      span.textContent = letter
      if (letter === ' ') {
        span.classList.add('letterspace')
      }
      span.classList.add('butler-msg-letter')
      if (popover_word) {
        popover_word.appendChild(span)
      }
      this.ttKey++ // to reposition popover properly
    },
    checkFinal () {
      if (this.finalLine) {
        this.$emit('lets-begin', false)
      }
    },
    addFinalButton () {
      const popover_text = this.$refs['butler-popover-text']
      const button_div = document.createElement('div')
      button_div.classList.add('butler-msg-button')
      button_div.innerHTML = '<button id="lets-begin-btn" class="btn btn-brand w-100">' + this.$gettext('Let\'s Begin!') + '</button>'
      if (popover_text) {
        popover_text.appendChild(button_div)
      }

      const letsBeginButton = document.getElementById('lets-begin-btn')
      if (letsBeginButton) {
        letsBeginButton.addEventListener('click', () => {
          this.$emit('lets-begin', false)
        })
      }
    },
    emptyMessageBox () {
      // empty message div
      this.renderedMessage = null
      const popover_text = this.$refs['butler-popover-text']
      if (popover_text) {
        popover_text.innerHTML = ''
      }
    },
  }
};
</script>

<style lang="scss">
#lets-begin-btn {
  margin-top: 1rem;
}
.butler-msg-word {
  display: inline-block;
  margin-left: 5px;
  .butler-msg-letter {
    display: inline-block;
    animation: letterappear 0.5s cubic-bezier(0.000, 0.125, 0.000, 1.650) 1;
    &.letterspace {
      width: 5px;
    }
  }
}

@keyframes letterappear {
  0% {
    opacity: 0;
    transform: translateX(10px) scale(0.7) rotate(30deg);
  }
  100% {
    opacity: 1;
    transform: rotate(0deg);
  }
}

.popover.b-popover {
  &.popover-butler {
    $popoverPadding: 1rem;
    padding: 0 $popoverPadding;
    max-width: 65%;
    min-height: 63px;
    transition: opacity 0.2s ease, height 0.2s ease;
    border-radius: $radiusLg;
    padding: 0rem;

    &.bs-popover-right { margin-left: 1rem; }
    &.bs-popover-bottom { 
      margin-top: 0px !important;
      top: 1.5rem !important;
    }
    .popover-body {
      width: 100%;
      height: 100%;
      padding: 0.5rem;
      .popover-text {
        font-size: 16px;
        line-height: 1.3;
        font-family: DINNextLTPro-Regular, sans-serif;
      }
    }
    @media (min-width: 576px) {
      max-width: 330px;
      &.placed {
        max-width: 500px;
      }
      .popover-body {
        padding: 1rem;
        .popover-text {
          font-size: 18px;
          line-height: 1.5;
        }
      }
    }
  }
}
</style>